import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
// Type-only (erased at build), so the value import that
// `publication-release-types.ts` makes of THIS module is not a runtime cycle.
// Keyed records off these unions are the point: a new release state or axis
// verdict becomes a compile error here instead of a raw enum on screen.
import type {
  PublicationReleaseState,
  PublicationVerificationStatus,
} from "@/features/publishing/publication-release-types";

// ---------------------------------------------------------------------------
// P2 — publish/deploy surface localization.
//
// WHY THIS EXISTS: the 2026-08-18 chat-journey census
// (`docs/superpowers/specs/2026-08-18-scenario-chat-journey-ux.md`, repair
// candidate #1) measured that on the three surfaces a Korean-speaking bank
// operator reaches when they commit to shipping — `publish-surface.tsx`,
// `deploy-surface.tsx`, and `publication-release-panel.tsx` — only the
// readiness-BLOCKER reasons were localized (`publish-gate-copy.ts`). Every
// other label, and the entire Launch surface's devops-audit-styled content,
// was hard-coded English. `publication-release-panel.tsx` (1,659 lines — the
// actual candidate/promote/deploy button surface) contained ZERO occurrences
// of `locale`/`useStudioLocale`: it was not partially localized, it had no
// locale awareness at all.
//
// This module is the single locale-keyed home for that surface's authored
// copy. It follows the same data/logic split the repo already uses in
// `studio-execution-copy.ts` (a `COPY` table per locale + a `get…(locale)`
// accessor) and `chat-messages-copy.ts` (co-located, one module per surface
// cluster). It lives under `src/i18n/` rather than beside one of the three
// consumers because all three read it — the same reason
// `studio-execution-copy.ts` lives here and is read from both
// `shell/ide-v2/` and `features/chat/`.
//
// `en` is byte-identical to the literals these files rendered before this
// change. That is deliberate: `DEFAULT_LOCALE` is `en`, and the existing
// `publish-surface.test.tsx` / `deploy-surface.test.tsx` /
// `publication-release-panel.test.tsx` / `publication-task-success.spec.ts`
// suites resolve controls by their English accessible name. A drift in `en`
// would show up as those suites failing, not as a silent regression.
//
// KEPT VERBATIM — deliberately NOT in this table, because translating them
// would break the operator's ability to cross-reference what they see on
// screen against a server record or a log line:
//  - Content hashes, release ids, deployment ids, candidate/output hashes,
//    `shortHash()` output, proof-artifact refs, receipt refs, attestation
//    refs. Identifiers, not copy.
//  - `humanizeAxis()` output — the fourteen publication verification AXIS
//    NAMES (`visual-baseline`, `live-server-readback`, …). These are the
//    devops audit field names an operator greps the server records and CI
//    output for; a translated axis name cannot be matched back to the
//    evidence it names.
//  - `axis.status` / `deployment.status` / `authorization.status` /
//    `readback.status` / `derivedState` / `publicationStateLabel()` — raw
//    server state enums, rendered in mono badges. This module localizes the
//    ABSENCE fallbacks Studio itself authors for them ("not started", "not
//    issued", …), never the server's own value.
//  - `release.blockers`, `deployment.failureReasons`,
//    `readback.failureReasons`, `axis.notes`, `targetProfile.openGaps`,
//    target-profile titles/ids/versions, and `AGENTIC_CANARY_ENV_NAMES` —
//    server- or registry-issued payload strings, authored elsewhere. Their
//    surrounding labels and headings ARE localized here.
//  - The canary provenance marker (`source: production-studio-readback`) and
//    check-output path (`output/agentic-rag-final-proof/evidence.json`) —
//    a literal marker value and a filesystem path an operator types.
//  - `presentPublicationReleaseError()`'s `message` field
//    (`publication-release-error.ts`) — it echoes `cause.message` verbatim,
//    a server- or exception-issued string an operator cross-references
//    against a log line. Its `title`/`recovery` strings (and the
//    no-cause-message `defaultMessage` fallback) ARE Studio's own authored
//    prose and are localized here (`release.errorBanner.kinds` /
//    `.defaultMessage`) as of the 2026-08-18 carry-over batch, item 1 — named
//    as the top residual in `.superpowers/p2-report.md`.
// ---------------------------------------------------------------------------

export interface StudioPublishDeployCopy {
  publish: {
    eyebrow: string;
    heading: string;
    intro: string;
    askLabel: string;
    preflightHeading: string;
    preflightDetail: string;
    savedProject: string;
    legacyStatus: string;
    slug: string;
    savedVersion: string;
    designSystemRelease: string;
    designSystemSnapshot: string;
    verifiedDeployment: string;
    lastProjectUpdate: string;
    noVerifiedReceipt: string;
  };
  launch: {
    eyebrow: string;
    heading: string;
    intro: string;
    askLabel: string;
    diagnosticsSummary: string;
    diagnosticsBadge: string;
    legacyStatus: string;
    slug: string;
    agentRuntimeCard: string;
    target: string;
    savedVersion: string;
    verifiedReleaseUrl: string;
    noVerifiedReceipt: string;
    liveCheck: string;
    noLiveCheckYet: string;
    refreshProof: string;
    refreshing: string;
    refreshNeedsSavedProject: string;
    refreshFailed: string;
    serviceHealthHeading: string;
    serviceHealthDetail: string;
  };
  canary: {
    heading: string;
    localProofReady: string;
    evidenceLabel: (source: string) => string;
    evidenceSources: Record<
      "manualImport" | "proofRecord" | "projectMetadata" | "none",
      string
    >;
    beforeProductionHeading: string;
    beforeProductionItems: readonly string[];
    requiredChecksHeading: string;
    requiredChecksItems: readonly string[];
    failClosedHeading: string;
    failClosedItems: readonly string[];
    missingChecksHeading: string;
    failClosedReasonsHeading: string;
    envNamesLabel: string;
    provenanceLabel: string;
    checkOutputLabel: string;
    jsonLabel: string;
    jsonPlaceholder: string;
    importButton: string;
    clearButton: string;
    libraryHub: string;
    diagnosticNote: string;
    savedLocallySyncFailed: string;
    clearedLocallySyncFailed: string;
  };
  release: {
    loading: string;
    candidateTitle: string;
    candidateDetail: string;
    launchTitle: string;
    launchDetail: string;
    refresh: string;
    notAvailable: string;
    /** Authored ABSENCE fallbacks for server status fields (never the value). */
    absent: Record<
      | "notIssued"
      | "notStarted"
      | "notSelected"
      | "notRegistered"
      | "noCandidate",
      string
    >;
    /**
     * `PublicationGateStatus` is a CLOSED two-value verdict derived
     * client-side by `deriveGate()` (publication-release-types.ts), not a
     * server passthrough — so unlike `deployment.status` /
     * `authorization.status` / `derivedState` it is localized here.
     */
    gateStatus: Record<"ready" | "blocked", string>;
    proofBoolean: Record<"match" | "mismatch" | "notChecked", string>;
    authority: {
      liveCheckPassed: string;
      rolledBack: string;
      takenDown: string;
      failClosed: string;
      passedDetail: string;
      failClosedDetail: string;
      productionReadyBadge: string;
    };
    errorBanner: {
      recoveryPrefix: string;
      refreshBeforeRetry: string;
      /**
       * `presentPublicationReleaseError()`'s (`publication-release-error.ts`)
       * OWN authored prose — Item 1 of the 2026-08-18 carry-over batch pulls
       * this presenter module in scope. `message` itself stays out of this
       * table: it echoes `cause.message` verbatim (a server- or
       * exception-issued string an operator cross-references against a log
       * line), so only the fallback used when no such message exists is
       * authored copy.
       */
      defaultMessage: string;
      /**
       * Slice 6 F7: the catalog failure's heading and recovery localized
       * because they come from this table; the BODY was minted in English in
       * `use-publication-release.ts` and read, verbatim on the ko appliance,
       * `Target profile catalog unavailable: No route matched …`. The cause
       * detail is still shown — localizing is not swallowing.
       */
      catalogMessage: (detail: string) => string;
      kinds: Record<
        | "authentication"
        | "permission"
        | "timeout"
        | "throttled"
        | "conflict"
        | "catalog"
        | "server"
        | "network"
        | "validation"
        | "unknown",
        { title: string; recovery: string }
      >;
    };
    empty: {
      openProjectTitle: string;
      openProjectDetail: string;
      noCandidateTitle: string;
      noCandidateDetail: string;
    };
    identity: {
      heading: string;
      release: string;
      revision: string;
      package: string;
      plan: string;
      manifest: string;
      promotion: string;
      signingHeading: string;
      unsignedNote: string;
      compiler: string;
      implementation: string;
      artifact: string;
      artifactHash: string;
      scope: string;
      signature: string;
      copyHashAria: string;
      copied: string;
      copyFailed: string;
      copyFullHash: string;
      signingUnavailable: string;
    };
    targetProfile: {
      label: string;
      none: string;
      adapterPrefix: string;
      openGaps: (count: number) => string;
      noGaps: string;
    };
    authorityCard: {
      heading: string;
      target: string;
      profile: string;
      adapter: string;
      authorization: string;
      deployment: string;
      liveCheck: string;
      openGapsHeading: string;
    };
    matrix: {
      heading: string;
      postDeployNote: string;
      candidateNote: string;
      candidateGate: string;
      productionGate: string;
      axisNotProduced: string;
      axisNoEvidence: string;
      /** Localized axis verdicts — the row used to print the wire enum. */
      axisStatus: Record<PublicationVerificationStatus, string>;
      /**
       * Slice 6 F4: fourteen axes each read a bare `pass` beside
       * `Proof artifacts / 0 immutable ref(s)`. An axis backed by no
       * immutable artifact is REPORTED passing, not proven passing, and must
       * not read identically to one that is.
       */
      axisReportedNotProven: string;
    };
    proof: {
      summary: string;
      contentAddressed: string;
      candidateEvidenceHeading: string;
      matrix: string;
      postDeployMatrixRef: string;
      candidateMatrixRef: string;
      signedRecord: string;
      promotion: string;
      candidateGate: string;
      productionGate: string;
      proofArtifacts: string;
      proofArtifactCount: (count: number) => string;
      receiptHeading: string;
      environment: string;
      operation: string;
      adapter: string;
      status: string;
      deploymentId: string;
      output: string;
      receipt: string;
      stagingGrant: string;
      authorization: string;
      verifiedUrlHeading: string;
      pendingUrlHeading: string;
      withheldNote: string;
      failureReasonsHeading: string;
      liveCheckHeading: string;
      artifactMatch: string;
      liveServer: string;
      checked: string;
      readback: string;
      noProofNote: string;
    };
    blockersHeading: string;
    environmentLegend: string;
    staging: string;
    production: string;
    /**
     * Shown INSTEAD of the environment radiogroup when the distribution has
     * only one deployment target (`appliance-publication-target.ts`). The
     * appliance's own control plane admits `authorityScope: "appliance-local"`
     * with `productionEligible: false` and refuses anything else, so there is
     * nothing to choose between and the surface states the target instead.
     */
    applianceTarget: {
      statement: string;
      detail: string;
    };
    /**
     * One label per `PublicationReleaseState`, keyed by the runtime array
     * that defines the union — so a new state is a compile error here (slice
     * 6 F3: the badge used to print `state.replaceAll("_", " ")`, identical
     * on the ko appliance and the en cloud build).
     */
    states: Record<PublicationReleaseState, string>;
    candidateReadyNote: string;
    noLaunchActionNote: string;
    /**
     * Slice 6 F1: `noLaunchActionNote` was printed one line ABOVE the
     * rollback and take-down buttons, because `selectPrimaryAction`'s
     * vocabulary of "launch action" does not include them. This is what the
     * same branch says when recovery actions ARE offered.
     */
    noForwardActionWithRecoveryNote: string;
    openVerifiedDeployment: string;
    rollbackButton: string;
    takeDownButton: string;
    confirm: {
      deployProductionTitle: string;
      deployStagingTitle: string;
      deployDetail: (
        profileId: string,
        manifestHash: string,
        environment: string,
      ) => string;
      rollbackTitle: string;
      rollbackDetail: (receiptHash: string) => string;
      takeDownTitle: (environment: string) => string;
      takeDownCurrent: string;
      takeDownDetail: (receiptHash: string) => string;
      closeAria: string;
      serverRecheckNote: string;
      cancel: string;
      confirmDeployment: string;
      confirmRollback: string;
      confirmTakeDown: string;
    };
    ledger: {
      summary: string;
      count: (count: number) => string;
      manifestPrefix: string;
      noPrior: string;
      serverOwnedNote: string;
    };
    actions: {
      prepareCandidate: string;
      prepareCandidateBusy: string;
      prepareForSelectedTarget: string;
      prepareUpdated: string;
      runMissingChecks: string;
      runMissingChecksBusy: string;
      refreshIndependentCheck: string;
      refreshIndependentCheckBusy: string;
      requestStagingGrant: string;
      requestStagingGrantBusy: string;
      deployStaging: string;
      deployStagingBusy: string;
      requestAuthorization: string;
      requestAuthorizationBusy: string;
      deployProduction: string;
      deployProductionBusy: string;
    };
    /** Thrown-and-shown guard messages inside confirmExternalEffect(). */
    guards: {
      noAdapter: string;
      productionNeedsPromotion: string;
      deployOutputManifestMissing: string;
      effectTargetReceiptMissing: string;
    };
  };
}

const en: StudioPublishDeployCopy = {
  publish: {
    eyebrow: "Publish · candidate preparation",
    heading: "Publish",
    intro:
      "Compile the saved project into a hash-bound candidate, run independent checks, and request promotion. No external deployment happens here.",
    askLabel: "Review publish readiness",
    preflightHeading: "Editor preflight",
    preflightDetail:
      "These client checks can block candidate preparation, but they cannot authorize deployment.",
    savedProject: "Saved project",
    legacyStatus: "Legacy status",
    slug: "Slug",
    savedVersion: "Saved version",
    designSystemRelease: "Design system release",
    designSystemSnapshot: "Design system snapshot",
    verifiedDeployment: "Verified deployment",
    lastProjectUpdate: "Last project update",
    noVerifiedReceipt: "No verified deployment receipt",
  },
  launch: {
    eyebrow: "Launch · authorized external effect",
    heading: "Launch",
    intro:
      "Request an environment-bound grant or production authorization, deploy the exact candidate, and finish the independent live check before calling it ready.",
    askLabel: "Review launch readiness",
    diagnosticsSummary: "Legacy project metadata and editor preflight",
    diagnosticsBadge: "diagnostic only",
    legacyStatus: "Legacy status",
    slug: "Slug",
    agentRuntimeCard: "App agent card",
    target: "Target",
    savedVersion: "Saved version",
    verifiedReleaseUrl: "Verified release URL",
    noVerifiedReceipt: "No verified deployment receipt",
    liveCheck: "Live check",
    noLiveCheckYet: "No live check yet",
    refreshProof: "Refresh proof",
    refreshing: "Refreshing",
    refreshNeedsSavedProject:
      "Open a saved server project to refresh the verification proof.",
    refreshFailed: "Could not refresh server proof records.",
    serviceHealthHeading: "Studio service health",
    serviceHealthDetail:
      "This canary diagnoses the Studio service itself. Imported JSON cannot satisfy a publication axis or authorize deployment.",
  },
  canary: {
    heading: "Agentic Studio Service Canary",
    localProofReady: "local proof ready",
    evidenceLabel: (source) => `evidence: ${source}`,
    evidenceSources: {
      manualImport: "manual import",
      proofRecord: "project proof record",
      projectMetadata: "project metadata",
      none: "none",
    },
    beforeProductionHeading: "Before Production",
    beforeProductionItems: [
      "8-file Agentic deterministic suite passes",
      "final browser proof passes with production canary deferred only for env",
      "production session is explicitly acknowledged before project creation",
    ],
    requiredChecksHeading: "Required Checks",
    requiredChecksItems: [
      "create and register one temporary production Studio project",
      "read back /api/projects/{projectId}/full as the source payload",
      "render what was read back, capture screenshots, and record cleanup",
    ],
    failClosedHeading: "Fail Closed",
    failClosedItems: [
      "missing production env vars",
      "local proof regression before production run",
      "verification, payload hash, design tokens, metadata, or cleanup proof missing",
    ],
    missingChecksHeading: "Missing checks",
    failClosedReasonsHeading: "Fail-Closed Reasons",
    envNamesLabel: "Required env names",
    provenanceLabel: "Provenance marker",
    checkOutputLabel: "Check output",
    jsonLabel: "Service check JSON",
    jsonPlaceholder:
      "Paste output/agentic-rag-final-proof/evidence.json without session tokens",
    importButton: "Import check",
    clearButton: "Clear check",
    libraryHub: "Open Library Hub",
    diagnosticNote:
      "Diagnostic only: this record cannot satisfy publication evidence or authorize deployment. Session tokens are runtime credentials; only paste readback facts, hashes, provenance, and cleanup proof.",
    savedLocallySyncFailed:
      "Check data is saved locally. Server record sync failed.",
    clearedLocallySyncFailed:
      "Check data was cleared locally. Server record sync failed.",
  },
  release: {
    loading: "Loading publication releases…",
    candidateTitle: "Publication candidate",
    candidateDetail:
      "Prepare the saved revision, verify all fourteen axes, and request promotion without deploying it.",
    launchTitle: "Authorized launch",
    launchDetail:
      "Deploy only a hash-bound candidate with the required grant or production authorization.",
    refresh: "Refresh",
    notAvailable: "not available",
    absent: {
      notIssued: "not issued",
      notStarted: "not started",
      notSelected: "not selected",
      notRegistered: "not registered",
      noCandidate: "no candidate",
    },
    gateStatus: {
      ready: "ready",
      blocked: "blocked",
    },
    proofBoolean: {
      match: "verified match",
      mismatch: "mismatch",
      notChecked: "not checked",
    },
    authority: {
      liveCheckPassed: "Live check passed",
      rolledBack: "Release rolled back · production fail-closed",
      takenDown: "Deployment taken down · production fail-closed",
      failClosed: "Production fail-closed",
      passedDetail:
        "The signed authorization, exact deployment receipt, artifact match, and live-server match are recorded for this release.",
      failClosedDetail:
        "A candidate, stored artifact, provider response, or HTTP response cannot create production authority. An independent, receipt-bound check of the live site must close every required proof.",
      productionReadyBadge: "production ready",
    },
    errorBanner: {
      recoveryPrefix: "Recovery:",
      refreshBeforeRetry: "Refresh release before retrying",
      defaultMessage: "Publication request failed.",
      catalogMessage: detail => `No registered target profile could be read, so no candidate can be prepared for a new target. Cause: ${detail}`,
      kinds: {
        authentication: {
          title: "Authentication required",
          recovery:
            "Restore the authenticated session, then refresh and reconcile this release before retrying. The browser cannot infer the external-effect outcome from this response.",
        },
        permission: {
          title: "Publication permission denied",
          recovery:
            "Confirm the project role, then refresh and reconcile this release before retrying with a release-authorized account.",
        },
        timeout: {
          title: "Publication status was not confirmed",
          recovery:
            "Refresh the immutable release before retrying. The same idempotent external effect may already have been accepted.",
        },
        throttled: {
          title: "Publication request is deferred",
          recovery:
            "Refresh the release first, then retry only the action the control plane still allows.",
        },
        conflict: {
          title: "Publication release changed",
          recovery:
            "Refresh the release projection. If the saved revision changed, prepare a new candidate before continuing.",
        },
        catalog: {
          title: "Target profile catalog unavailable",
          recovery:
            "Existing release evidence remains visible. Refresh before preparing a candidate for a different target.",
        },
        server: {
          title: "Publication control plane unavailable",
          recovery:
            "Refresh the release before retrying. An external effect is not considered failed until the server records a definitive result.",
        },
        network: {
          title: "Publication connection interrupted",
          recovery:
            "Restore the connection and refresh the release before retrying so an ambiguous provider result is reconciled.",
        },
        validation: {
          title: "Publication request rejected",
          recovery:
            "Review the release blockers and exact target/evidence bindings, then run only the newly allowed action.",
        },
        unknown: {
          title: "Publication action failed",
          recovery:
            "Refresh the release and review its blockers before retrying. Production remains fail-closed.",
        },
      },
    },
    empty: {
      openProjectTitle: "Open a saved project",
      openProjectDetail:
        "Publication candidates are bound to a durable project revision.",
      noCandidateTitle: "No publication candidate",
      noCandidateDetail:
        "Prepare the current saved revision before selecting an environment or deployment target.",
    },
    identity: {
      heading: "Candidate identity",
      release: "Release",
      revision: "Revision",
      package: "Package",
      plan: "Plan",
      manifest: "Manifest",
      promotion: "Promotion",
      signingHeading: "Compiler artifact signing",
      unsignedNote:
        "Candidate scope · unsigned. This identity does not claim a released or signed compiler artifact.",
      compiler: "Compiler",
      implementation: "Implementation",
      artifact: "Artifact",
      artifactHash: "Artifact hash",
      scope: "Scope",
      signature: "Signature",
      copyHashAria: "Copy compiler artifact hash",
      copied: "Copied",
      copyFailed: "Copy failed",
      copyFullHash: "Copy full hash",
      signingUnavailable:
        "Compiler signing details are unavailable. This older release did not record which compiler built it, so Studio will not guess it from the target profile.",
    },
    targetProfile: {
      label: "Target profile",
      none: "No registered target profile",
      adapterPrefix: "Adapter:",
      openGaps: (count) => `${count} open production gap(s)`,
      noGaps: "no declared profile gaps",
    },
    authorityCard: {
      heading: "Authority and external effect",
      target: "Target",
      profile: "Profile",
      adapter: "Adapter",
      authorization: "Authorization",
      deployment: "Deployment",
      liveCheck: "Live check",
      openGapsHeading: "Open production gaps",
    },
    matrix: {
      heading: "Publication verification",
      postDeployNote:
        "Production uses the content-addressed staging certification matrix. Evidence links are hash-bound server records.",
      candidateNote:
        "Candidate and production gates remain separate. Evidence links are hash-bound server records.",
      candidateGate: "Candidate",
      productionGate: "Production",
      axisNotProduced: "Candidate has not produced this axis result.",
      axisNoEvidence: "No trusted evidence has been recorded.",
      axisStatus: {
        pass: "pass",
        fail: "fail",
        "not-proven": "not produced",
      },
      axisReportedNotProven: "Reported passing · no immutable proof",
    },
    proof: {
      summary: "Exact proof, authority, and live-check details",
      contentAddressed: "content-addressed",
      candidateEvidenceHeading: "Candidate evidence",
      matrix: "Matrix",
      postDeployMatrixRef: "post-deploy verification matrix",
      candidateMatrixRef: "candidate verification matrix",
      signedRecord: "Signed record",
      promotion: "Promotion",
      candidateGate: "Candidate gate",
      productionGate: "Production gate",
      proofArtifacts: "Proof artifacts",
      proofArtifactCount: (count) => `${count} immutable ref(s)`,
      receiptHeading: "Execution and deployment receipt",
      environment: "Environment",
      operation: "Operation",
      adapter: "Adapter",
      status: "Status",
      deploymentId: "Deployment ID",
      output: "Output",
      receipt: "Receipt",
      stagingGrant: "Staging grant",
      authorization: "Authorization",
      verifiedUrlHeading: "Verified deployment URL",
      pendingUrlHeading: "Provider reported a URL · live check pending",
      withheldNote:
        "The address is withheld until the exact receipt, artifact, and live-server check all pass.",
      failureReasonsHeading: "Failure reasons",
      liveCheckHeading: "Independent live check",
      artifactMatch: "Artifact match",
      liveServer: "Live server",
      checked: "Checked",
      readback: "Readback",
      noProofNote:
        "No deployment or live-server proof has been recorded. Production remains fail-closed.",
    },
    blockersHeading: "Remaining blockers",
    environmentLegend: "Deployment environment",
    staging: "Staging",
    production: "Production",
    applianceTarget: {
      statement: "This appliance is the only deployment target.",
      detail:
        "A publication is verified and served on this box under appliance-local authority. There is no separate production site here, and promotion beyond the appliance is performed outside it.",
    },
    states: {
        draft: "Draft",
        compiling: "Compiling",
        candidate_blocked: "Candidate blocked",
        candidate_ready: "Candidate ready",
        verification_running: "Verification running",
        verification_blocked: "Verification blocked",
        certification_candidate: "Certification candidate",
        authorization_pending: "Authorization pending",
        authorized: "Authorized",
        deploying: "Deploying",
        deployment_readback_pending: "Deployment read-back pending",
        live_readback_pending: "Live read-back pending",
        production_ready: "Production ready",
        failed: "Failed",
        rolled_back: "Rolled back",
        taken_down: "Taken down",
    },
    candidateReadyNote: "The candidate is ready for the Launch surface.",
    noLaunchActionNote:
      "No launch action is currently allowed. Review the blockers and refresh the release.",
    noForwardActionWithRecoveryNote:
      "No forward launch action is available for this release. The actions below change what is already live.",
    openVerifiedDeployment: "Open verified deployment",
    rollbackButton: "Roll back to prior verified receipt",
    takeDownButton: "Take down verified deployment",
    confirm: {
      deployProductionTitle: "Deploy to production?",
      deployStagingTitle: "Deploy staging candidate?",
      deployDetail: (profileId, manifestHash, environment) =>
        `${profileId} will deploy ${manifestHash} to ${environment}.`,
      rollbackTitle: "Roll back this release?",
      rollbackDetail: (receiptHash) =>
        `The prior verified receipt ${receiptHash} will be restored and read back again.`,
      takeDownTitle: (environment) =>
        `Take down this ${environment} deployment?`,
      takeDownCurrent: "current",
      takeDownDetail: (receiptHash) =>
        `Routing for receipt ${receiptHash} will be tombstoned. Immutable release artifacts remain preserved for audit and recovery.`,
      closeAria: "Close confirmation",
      serverRecheckNote:
        "The server will re-check the signed grant or authorization, source revision, target, and output manifest before any external write.",
      cancel: "Cancel",
      confirmDeployment: "Confirm deployment",
      confirmRollback: "Confirm rollback",
      confirmTakeDown: "Confirm take-down",
    },
    ledger: {
      summary: "Immutable release ledger",
      count: (count) => `${count} release${count === 1 ? "" : "s"}`,
      manifestPrefix: "manifest",
      noPrior:
        "No prior release exists. The current immutable release identity is shown above.",
      serverOwnedNote:
        "This history is owned by the server. Browser caches and imported JSON cannot add a release or change a proof result.",
    },
    actions: {
      prepareCandidate: "Prepare candidate",
      prepareCandidateBusy: "Preparing candidate…",
      prepareForSelectedTarget: "Prepare candidate for selected target",
      prepareUpdated: "Prepare updated candidate",
      runMissingChecks: "Run missing checks",
      runMissingChecksBusy: "Running verification…",
      refreshIndependentCheck: "Refresh independent check",
      refreshIndependentCheckBusy: "Reading deployed release…",
      requestStagingGrant: "Request staging grant",
      requestStagingGrantBusy: "Requesting staging grant…",
      deployStaging: "Deploy staging candidate",
      deployStagingBusy: "Deploying staging…",
      requestAuthorization: "Request production authorization",
      requestAuthorizationBusy: "Requesting authorization…",
      deployProduction: "Deploy authorized release",
      deployProductionBusy: "Deploying production release…",
    },
    guards: {
      noAdapter:
        "The selected target profile has no registered deployment adapter.",
      productionNeedsPromotion:
        "Production deployment requires an exact staging promotion reference.",
      deployOutputManifestMissing:
        "This release no longer carries its verification output manifest, so the deploy request was not sent. Refresh the release and prepare a new candidate.",
      effectTargetReceiptMissing:
        "The target receipt for this action is missing, so no request was sent. Refresh the release.",
    },
  },
};

const ko: StudioPublishDeployCopy = {
  publish: {
    eyebrow: "게시 · 후보본 준비",
    heading: "게시",
    intro:
      "저장된 프로젝트를 해시로 고정된 후보본으로 만들고, 독립 검사를 실행한 뒤 승격을 요청합니다. 이 화면에서는 외부 배포가 일어나지 않습니다.",
    askLabel: "게시 준비 상태 살펴보기",
    preflightHeading: "편집기 사전 점검",
    preflightDetail:
      "이 점검은 후보본 준비를 막을 수 있지만, 배포를 승인하지는 못합니다.",
    savedProject: "저장된 프로젝트",
    legacyStatus: "이전 방식 상태",
    slug: "주소 이름",
    savedVersion: "저장된 버전",
    designSystemRelease: "디자인 시스템 릴리스",
    designSystemSnapshot: "디자인 시스템 스냅샷",
    verifiedDeployment: "검증된 배포",
    lastProjectUpdate: "마지막 프로젝트 수정",
    noVerifiedReceipt: "검증된 배포 영수증이 없습니다",
  },
  launch: {
    eyebrow: "실서비스 시작 · 승인된 외부 반영",
    heading: "실서비스 시작",
    intro:
      "환경에 묶인 사용 권한이나 운영 승인을 요청하고, 바로 그 후보본을 배포한 뒤 독립 실동작 확인까지 끝내야 준비 완료입니다.",
    askLabel: "시작 준비 상태 살펴보기",
    diagnosticsSummary: "이전 방식 프로젝트 정보와 편집기 사전 점검",
    diagnosticsBadge: "진단용",
    legacyStatus: "이전 방식 상태",
    slug: "주소 이름",
    agentRuntimeCard: "앱 에이전트 카드",
    target: "대상",
    savedVersion: "저장된 버전",
    verifiedReleaseUrl: "검증된 릴리스 주소",
    noVerifiedReceipt: "검증된 배포 영수증이 없습니다",
    liveCheck: "실동작 확인",
    noLiveCheckYet: "아직 실동작 확인이 없습니다",
    refreshProof: "증거 새로 읽기",
    refreshing: "새로 읽는 중",
    refreshNeedsSavedProject:
      "검증 증거를 새로 읽으려면 서버에 저장된 프로젝트를 열어 주세요.",
    refreshFailed: "서버 증거 기록을 새로 읽지 못했습니다.",
    serviceHealthHeading: "Studio 서비스 상태",
    serviceHealthDetail:
      "이 점검은 Studio 서비스 자체를 진단합니다. 가져온 JSON은 게시 검증 항목을 대신하거나 배포를 승인할 수 없습니다.",
  },
  canary: {
    heading: "Studio 서비스 상시 점검",
    localProofReady: "로컬 증거 준비됨",
    evidenceLabel: (source) => `증거 출처: ${source}`,
    evidenceSources: {
      manualImport: "직접 가져옴",
      proofRecord: "프로젝트 증거 기록",
      projectMetadata: "프로젝트 정보",
      none: "없음",
    },
    beforeProductionHeading: "운영 반영 전",
    beforeProductionItems: [
      "8개 파일 결정적 검사 전부 통과",
      "마지막 브라우저 증거 통과, 운영 상시 점검만 환경 변수 때문에 보류",
      "프로젝트를 만들기 전에 운영 세션임을 분명히 확인",
    ],
    requiredChecksHeading: "필수 점검",
    requiredChecksItems: [
      "임시 운영 Studio 프로젝트 하나를 만들고 등록",
      "/api/projects/{projectId}/full 을 원본 자료로 다시 읽기",
      "다시 읽은 내용을 그려 보고, 화면을 저장하고, 정리 결과까지 기록",
    ],
    failClosedHeading: "막고 멈추는 조건",
    failClosedItems: [
      "운영 환경 변수 누락",
      "운영 실행 전 로컬 증거가 뒤로 후퇴",
      "검증·자료 해시·디자인 토큰·부가 정보·정리 증거 중 하나라도 없음",
    ],
    missingChecksHeading: "빠진 점검",
    failClosedReasonsHeading: "막고 멈춘 이유",
    envNamesLabel: "필요한 환경 변수 이름",
    provenanceLabel: "출처 표시",
    checkOutputLabel: "점검 결과 파일",
    jsonLabel: "서비스 점검 JSON",
    jsonPlaceholder:
      "output/agentic-rag-final-proof/evidence.json 내용을 세션 토큰 없이 붙여넣으세요",
    importButton: "점검 가져오기",
    clearButton: "점검 비우기",
    libraryHub: "자료실 열기",
    diagnosticNote:
      "진단용입니다. 이 기록은 게시 증거를 대신하거나 배포를 승인할 수 없습니다. 세션 토큰은 실행 자격 증명이므로, 다시 읽은 사실·해시·출처·정리 증거만 붙여넣으세요.",
    savedLocallySyncFailed:
      "점검 자료는 이 기기에 저장됐습니다. 서버 기록 동기화는 실패했습니다.",
    clearedLocallySyncFailed:
      "점검 자료는 이 기기에서 지웠습니다. 서버 기록 동기화는 실패했습니다.",
  },
  release: {
    loading: "게시 릴리스를 불러오는 중…",
    candidateTitle: "게시 후보본",
    candidateDetail:
      "저장된 판을 준비하고, 14개 검증 항목을 모두 확인한 뒤, 배포 없이 승격을 요청하세요.",
    launchTitle: "승인된 실서비스 시작",
    launchDetail:
      "해시로 고정된 후보본만, 필요한 사용 권한이나 운영 승인을 갖춘 상태로 배포합니다.",
    refresh: "새로 읽기",
    notAvailable: "값 없음",
    absent: {
      notIssued: "발급되지 않음",
      notStarted: "시작되지 않음",
      notSelected: "선택되지 않음",
      notRegistered: "등록되지 않음",
      noCandidate: "후보본 없음",
    },
    gateStatus: {
      ready: "준비됨",
      blocked: "막힘",
    },
    proofBoolean: {
      match: "검증되어 일치",
      mismatch: "불일치",
      notChecked: "확인하지 않음",
    },
    authority: {
      liveCheckPassed: "실동작 확인 통과",
      rolledBack: "릴리스를 되돌렸습니다 · 운영은 막힌 상태",
      takenDown: "배포를 내렸습니다 · 운영은 막힌 상태",
      failClosed: "운영은 막힌 상태",
      passedDetail:
        "서명된 승인, 정확한 배포 영수증, 산출물 일치, 실서버 일치가 이 릴리스에 모두 기록돼 있습니다.",
      failClosedDetail:
        "후보본, 저장된 산출물, 공급자 응답, HTTP 응답만으로는 운영 권한이 생기지 않습니다. 영수증에 묶인 독립 실사이트 확인이 필요한 증거를 모두 닫아야 합니다.",
      productionReadyBadge: "운영 준비됨",
    },
    errorBanner: {
      recoveryPrefix: "복구 방법:",
      refreshBeforeRetry: "다시 시도하기 전에 릴리스를 새로 읽으세요",
      defaultMessage: "게시 요청이 실패했습니다.",
      catalogMessage: detail => `등록된 대상 프로필을 읽지 못해, 새 대상으로는 후보본을 준비할 수 없습니다. 원인: ${detail}`,
      kinds: {
        authentication: {
          title: "인증이 필요합니다",
          recovery:
            "인증된 세션을 복구한 뒤, 다시 시도하기 전에 릴리스를 새로 읽어 반영하세요. 브라우저는 이 응답만으로 외부 반영 결과를 알 수 없습니다.",
        },
        permission: {
          title: "게시 권한이 없습니다",
          recovery:
            "프로젝트 내 역할을 확인한 뒤, 게시 권한이 있는 계정으로 다시 시도하기 전에 릴리스를 새로 읽어 반영하세요.",
        },
        timeout: {
          title: "게시 상태를 확인하지 못했습니다",
          recovery:
            "다시 시도하기 전에 변경 불가 릴리스를 새로 읽으세요. 같은 멱등 외부 반영이 이미 받아들여졌을 수 있습니다.",
        },
        throttled: {
          title: "게시 요청이 미뤄졌습니다",
          recovery:
            "먼저 릴리스를 새로 읽은 뒤, 제어 영역이 아직 허용하는 동작만 다시 시도하세요.",
        },
        conflict: {
          title: "게시 릴리스가 바뀌었습니다",
          recovery:
            "릴리스 상태를 새로 읽으세요. 저장된 판이 바뀌었다면 계속하기 전에 새 후보본을 준비하세요.",
        },
        catalog: {
          title: "대상 프로필 카탈로그를 사용할 수 없습니다",
          recovery:
            "기존 릴리스 증거는 그대로 보입니다. 다른 대상으로 후보본을 준비하기 전에 새로 읽으세요.",
        },
        server: {
          title: "게시 제어 영역을 사용할 수 없습니다",
          recovery:
            "다시 시도하기 전에 릴리스를 새로 읽으세요. 서버가 확정된 결과를 기록하기 전까지는 외부 반영이 실패했다고 볼 수 없습니다.",
        },
        network: {
          title: "게시 연결이 끊겼습니다",
          recovery:
            "연결을 복구한 뒤, 애매한 공급자 응답을 정리하기 위해 다시 시도하기 전에 릴리스를 새로 읽으세요.",
        },
        validation: {
          title: "게시 요청이 거부되었습니다",
          recovery:
            "남은 걸림돌과 정확한 대상/증거 연결을 확인한 뒤, 새로 허용된 동작만 실행하세요.",
        },
        unknown: {
          title: "게시 작업이 실패했습니다",
          recovery:
            "다시 시도하기 전에 릴리스를 새로 읽고 걸림돌을 확인하세요. 운영은 계속 막힌 상태입니다.",
        },
      },
    },
    empty: {
      openProjectTitle: "저장된 프로젝트를 열어 주세요",
      openProjectDetail:
        "게시 후보본은 서버에 저장된 프로젝트 판에 묶여 있습니다.",
      noCandidateTitle: "게시 후보본이 없습니다",
      noCandidateDetail:
        "환경이나 배포 대상을 고르기 전에, 지금 저장된 판으로 후보본을 준비하세요.",
    },
    identity: {
      heading: "후보본 신원",
      release: "릴리스",
      revision: "판",
      package: "패키지",
      plan: "계획",
      manifest: "구성 목록",
      promotion: "승격",
      signingHeading: "컴파일러 산출물 서명",
      unsignedNote:
        "후보본 범위 · 서명 없음. 이 신원은 배포된 컴파일러 산출물이나 서명된 산출물을 주장하지 않습니다.",
      compiler: "컴파일러",
      implementation: "구현",
      artifact: "산출물",
      artifactHash: "산출물 해시",
      scope: "범위",
      signature: "서명",
      copyHashAria: "컴파일러 산출물 해시 복사",
      copied: "복사했습니다",
      copyFailed: "복사하지 못했습니다",
      copyFullHash: "전체 해시 복사",
      signingUnavailable:
        "컴파일러 서명 정보가 없습니다. 이 예전 릴리스는 어떤 컴파일러로 만들었는지 기록하지 않았고, Studio는 대상 프로필을 보고 짐작하지 않습니다.",
    },
    targetProfile: {
      label: "대상 프로필",
      none: "등록된 대상 프로필이 없습니다",
      adapterPrefix: "연결 방식:",
      openGaps: (count) => `해결되지 않은 운영 공백 ${count}건`,
      noGaps: "프로필에 명시된 공백 없음",
    },
    authorityCard: {
      heading: "권한과 외부 반영",
      target: "대상",
      profile: "프로필",
      adapter: "연결 방식",
      authorization: "운영 승인",
      deployment: "배포",
      liveCheck: "실동작 확인",
      openGapsHeading: "해결되지 않은 운영 공백",
    },
    matrix: {
      heading: "게시 검증",
      postDeployNote:
        "운영은 내용 주소로 고정된 스테이징 인증 표를 사용합니다. 증거 링크는 해시에 묶인 서버 기록입니다.",
      candidateNote:
        "후보본 관문과 운영 관문은 서로 분리돼 있습니다. 증거 링크는 해시에 묶인 서버 기록입니다.",
      candidateGate: "후보본",
      productionGate: "운영",
      axisNotProduced: "후보본이 이 항목의 결과를 아직 만들지 않았습니다.",
      axisNoEvidence: "믿을 수 있는 증거가 기록되지 않았습니다.",
      axisStatus: {
        pass: "통과",
        fail: "실패",
        "not-proven": "생성되지 않음",
      },
      axisReportedNotProven: "통과로 보고됨 · 불변 증거 없음",
    },
    proof: {
      summary: "정확한 증거, 권한, 실동작 확인 상세",
      contentAddressed: "내용 주소 고정",
      candidateEvidenceHeading: "후보본 증거",
      matrix: "검증표",
      postDeployMatrixRef: "배포 후 검증표",
      candidateMatrixRef: "후보본 검증표",
      signedRecord: "서명된 기록",
      promotion: "승격",
      candidateGate: "후보본 관문",
      productionGate: "운영 관문",
      proofArtifacts: "증거 산출물",
      proofArtifactCount: (count) => `변경 불가 참조 ${count}건`,
      receiptHeading: "실행과 배포 영수증",
      environment: "환경",
      operation: "작업",
      adapter: "연결 방식",
      status: "상태",
      deploymentId: "배포 번호",
      output: "산출물",
      receipt: "영수증",
      stagingGrant: "스테이징 사용 권한",
      authorization: "운영 승인",
      verifiedUrlHeading: "검증된 배포 주소",
      pendingUrlHeading: "공급자가 주소를 알려줬습니다 · 실동작 확인 대기",
      withheldNote:
        "정확한 영수증, 산출물, 실서버 확인이 모두 통과할 때까지 주소를 보여주지 않습니다.",
      failureReasonsHeading: "실패 이유",
      liveCheckHeading: "독립 실동작 확인",
      artifactMatch: "산출물 일치",
      liveServer: "실서버",
      checked: "확인 시각",
      readback: "다시 읽기",
      noProofNote:
        "배포나 실서버 증거가 기록되지 않았습니다. 운영은 계속 막힌 상태입니다.",
    },
    blockersHeading: "남은 걸림돌",
    environmentLegend: "배포 환경",
    staging: "스테이징",
    production: "운영",
    applianceTarget: {
      statement: "이 어플라이언스가 유일한 배포 대상입니다.",
      detail:
        "발행물은 이 장비 안에서 appliance-local 권한으로 검증되고 서빙됩니다. 여기에는 별도의 운영 사이트가 없으며, 어플라이언스 밖으로의 승격은 이 장비 바깥에서 이뤄집니다.",
    },
    states: {
        draft: "초안",
        compiling: "컴파일 중",
        candidate_blocked: "후보본 막힘",
        candidate_ready: "후보본 준비됨",
        verification_running: "검증 진행 중",
        verification_blocked: "검증 막힘",
        certification_candidate: "인증 후보본",
        authorization_pending: "인가 대기",
        authorized: "인가됨",
        deploying: "배포 중",
        deployment_readback_pending: "배포 재확인 대기",
        live_readback_pending: "라이브 재확인 대기",
        production_ready: "운영 준비됨",
        failed: "실패",
        rolled_back: "되돌림",
        taken_down: "내려감",
    },
    candidateReadyNote: "후보본은 실서비스 시작 화면에서 쓸 준비가 됐습니다.",
    noLaunchActionNote:
      "지금 할 수 있는 시작 동작이 없습니다. 걸림돌을 확인하고 릴리스를 새로 읽어 주세요.",
    noForwardActionWithRecoveryNote:
      "이 릴리스로 앞으로 나아가는 시작 동작은 없습니다. 아래 동작은 이미 라이브인 것을 바꿉니다.",
    openVerifiedDeployment: "검증된 배포 열기",
    rollbackButton: "이전에 검증된 영수증으로 되돌리기",
    takeDownButton: "검증된 배포 내리기",
    confirm: {
      deployProductionTitle: "운영에 배포할까요?",
      deployStagingTitle: "스테이징 후보본을 배포할까요?",
      deployDetail: (profileId, manifestHash, environment) =>
        `${profileId} 프로필로 ${manifestHash} 을(를) ${environment} 환경에 배포합니다.`,
      rollbackTitle: "이 릴리스를 되돌릴까요?",
      rollbackDetail: (receiptHash) =>
        `이전에 검증된 영수증 ${receiptHash} 을(를) 되살리고 다시 확인합니다.`,
      takeDownTitle: (environment) => `이 ${environment} 배포를 내릴까요?`,
      takeDownCurrent: "현재",
      takeDownDetail: (receiptHash) =>
        `영수증 ${receiptHash} 의 연결 경로를 닫습니다. 변경 불가 릴리스 산출물은 감사와 복구를 위해 그대로 남습니다.`,
      closeAria: "확인 창 닫기",
      serverRecheckNote:
        "서버가 서명된 사용 권한이나 운영 승인, 원본 판, 대상, 산출물 목록을 다시 확인한 뒤에야 외부에 씁니다.",
      cancel: "취소",
      confirmDeployment: "배포 확인",
      confirmRollback: "되돌리기 확인",
      confirmTakeDown: "내리기 확인",
    },
    ledger: {
      summary: "변경 불가 릴리스 이력",
      count: (count) => `릴리스 ${count}건`,
      manifestPrefix: "구성 목록",
      noPrior:
        "이전 릴리스가 없습니다. 지금의 변경 불가 릴리스 신원은 위에 있습니다.",
      serverOwnedNote:
        "이 이력은 서버가 관리합니다. 브라우저 저장 내용이나 가져온 JSON으로는 릴리스를 더하거나 증거 결과를 바꿀 수 없습니다.",
    },
    actions: {
      prepareCandidate: "후보본 준비",
      prepareCandidateBusy: "후보본 준비 중…",
      prepareForSelectedTarget: "선택한 대상으로 후보본 준비",
      prepareUpdated: "최신 내용으로 후보본 다시 준비",
      runMissingChecks: "빠진 점검 실행",
      runMissingChecksBusy: "검증 실행 중…",
      refreshIndependentCheck: "독립 확인 다시 실행",
      refreshIndependentCheckBusy: "배포된 릴리스를 읽는 중…",
      requestStagingGrant: "스테이징 사용 권한 요청",
      requestStagingGrantBusy: "스테이징 사용 권한 요청 중…",
      deployStaging: "스테이징 후보본 배포",
      deployStagingBusy: "스테이징 배포 중…",
      requestAuthorization: "운영 승인 요청",
      requestAuthorizationBusy: "운영 승인 요청 중…",
      deployProduction: "승인된 릴리스 배포",
      deployProductionBusy: "운영 릴리스 배포 중…",
    },
    guards: {
      noAdapter: "선택한 대상 프로필에 등록된 배포 연결 방식이 없습니다.",
      productionNeedsPromotion:
        "운영 배포에는 정확한 스테이징 승격 참조가 필요합니다.",
      deployOutputManifestMissing:
        "이 릴리스에 검증 산출물 매니페스트가 더 이상 없어 배포 요청을 보내지 않았습니다. 릴리스를 새로 읽고 후보를 다시 준비해 주세요.",
      effectTargetReceiptMissing:
        "이 동작의 대상 영수증이 없어 아무 요청도 보내지 않았습니다. 릴리스를 새로 읽어 주세요.",
    },
  },
};

const ja: StudioPublishDeployCopy = {
  publish: {
    eyebrow: "公開 · 候補の準備",
    heading: "公開",
    intro:
      "保存済みプロジェクトをハッシュで固定した候補にまとめ、独立した検査を実行して昇格を申請します。この画面では外部への配信は行われません。",
    askLabel: "公開準備の状況を確認",
    preflightHeading: "エディター事前チェック",
    preflightDetail:
      "このチェックは候補の準備を止められますが、配信を承認することはできません。",
    savedProject: "保存済みプロジェクト",
    legacyStatus: "旧方式のステータス",
    slug: "アドレス名",
    savedVersion: "保存済みバージョン",
    designSystemRelease: "デザインシステムのリリース",
    designSystemSnapshot: "デザインシステムのスナップショット",
    verifiedDeployment: "検証済みの配信",
    lastProjectUpdate: "最後の更新",
    noVerifiedReceipt: "検証済みの配信レシートがありません",
  },
  launch: {
    eyebrow: "本番開始 · 承認された外部反映",
    heading: "本番開始",
    intro:
      "環境に紐づく利用許可または本番承認を申請し、その候補をそのまま配信し、独立した実動作チェックまで終えて初めて準備完了です。",
    askLabel: "開始準備の状況を確認",
    diagnosticsSummary: "旧方式のプロジェクト情報とエディター事前チェック",
    diagnosticsBadge: "診断用",
    legacyStatus: "旧方式のステータス",
    slug: "アドレス名",
    agentRuntimeCard: "アプリエージェントカード",
    target: "対象",
    savedVersion: "保存済みバージョン",
    verifiedReleaseUrl: "検証済みリリースのアドレス",
    noVerifiedReceipt: "検証済みの配信レシートがありません",
    liveCheck: "実動作チェック",
    noLiveCheckYet: "実動作チェックはまだありません",
    refreshProof: "証拠を読み直す",
    refreshing: "読み直し中",
    refreshNeedsSavedProject:
      "検証の証拠を読み直すには、サーバーに保存されたプロジェクトを開いてください。",
    refreshFailed: "サーバーの証拠記録を読み直せませんでした。",
    serviceHealthHeading: "Studio サービスの状態",
    serviceHealthDetail:
      "このチェックは Studio サービス自体を診断します。取り込んだ JSON は公開の検証項目を満たさず、配信を承認できません。",
  },
  canary: {
    heading: "Studio サービス常時チェック",
    localProofReady: "ローカル証拠あり",
    evidenceLabel: (source) => `証拠の出どころ: ${source}`,
    evidenceSources: {
      manualImport: "手動で取り込み",
      proofRecord: "プロジェクトの証拠記録",
      projectMetadata: "プロジェクト情報",
      none: "なし",
    },
    beforeProductionHeading: "本番反映の前に",
    beforeProductionItems: [
      "8ファイルの決定的な検査がすべて合格",
      "最後のブラウザ証拠が合格し、本番常時チェックのみ環境変数のため保留",
      "プロジェクト作成前に本番セッションであることを明示的に確認",
    ],
    requiredChecksHeading: "必須チェック",
    requiredChecksItems: [
      "一時的な本番 Studio プロジェクトを1つ作成して登録",
      "/api/projects/{projectId}/full を元データとして読み直す",
      "読み直した内容を描画し、画面を保存し、後片付けまで記録",
    ],
    failClosedHeading: "止める条件",
    failClosedItems: [
      "本番の環境変数が不足",
      "本番実行の前にローカル証拠が後退",
      "検証・データのハッシュ・デザイントークン・付帯情報・後片付けの証拠のいずれかが欠落",
    ],
    missingChecksHeading: "欠けているチェック",
    failClosedReasonsHeading: "止めた理由",
    envNamesLabel: "必要な環境変数名",
    provenanceLabel: "出どころの印",
    checkOutputLabel: "チェック結果ファイル",
    jsonLabel: "サービスチェック JSON",
    jsonPlaceholder:
      "output/agentic-rag-final-proof/evidence.json の内容をセッショントークンなしで貼り付けてください",
    importButton: "チェックを取り込む",
    clearButton: "チェックを消す",
    libraryHub: "資料庫を開く",
    diagnosticNote:
      "診断用です。この記録は公開の証拠を満たさず、配信を承認できません。セッショントークンは実行資格情報なので、読み直した事実・ハッシュ・出どころ・後片付けの証拠だけを貼り付けてください。",
    savedLocallySyncFailed:
      "チェックデータはこの端末に保存しました。サーバー記録の同期は失敗しました。",
    clearedLocallySyncFailed:
      "チェックデータはこの端末から消しました。サーバー記録の同期は失敗しました。",
  },
  release: {
    loading: "公開リリースを読み込み中…",
    candidateTitle: "公開候補",
    candidateDetail:
      "保存済みの版を準備し、14個の検証項目をすべて確認し、配信せずに昇格を申請します。",
    launchTitle: "承認された本番開始",
    launchDetail:
      "ハッシュで固定された候補だけを、必要な利用許可または本番承認とともに配信します。",
    refresh: "読み直す",
    notAvailable: "値なし",
    absent: {
      notIssued: "未発行",
      notStarted: "未開始",
      notSelected: "未選択",
      notRegistered: "未登録",
      noCandidate: "候補なし",
    },
    gateStatus: {
      ready: "準備済み",
      blocked: "止まっている",
    },
    proofBoolean: {
      match: "検証済みで一致",
      mismatch: "不一致",
      notChecked: "未確認",
    },
    authority: {
      liveCheckPassed: "実動作チェック合格",
      rolledBack: "リリースを戻しました · 本番は止まった状態",
      takenDown: "配信を取り下げました · 本番は止まった状態",
      failClosed: "本番は止まった状態",
      passedDetail:
        "署名された承認、正確な配信レシート、成果物の一致、実サーバーの一致がこのリリースにすべて記録されています。",
      failClosedDetail:
        "候補、保存された成果物、提供元の応答、HTTP 応答だけでは本番の権限は生まれません。レシートに紐づく独立した実サイト確認が、必要な証拠をすべて閉じる必要があります。",
      productionReadyBadge: "本番準備済み",
    },
    errorBanner: {
      recoveryPrefix: "復旧方法:",
      refreshBeforeRetry: "やり直す前にリリースを読み直してください",
      defaultMessage: "公開リクエストが失敗しました。",
      catalogMessage: detail => `登録された対象プロファイルを読み取れないため、新しい対象では候補を準備できません。原因: ${detail}`,
      kinds: {
        authentication: {
          title: "認証が必要です",
          recovery:
            "認証済みセッションを復元し、再試行する前にリリースを読み直して反映してください。ブラウザはこの応答だけでは外部反映の結果を推測できません。",
        },
        permission: {
          title: "公開の権限がありません",
          recovery:
            "プロジェクトの役割を確認し、公開権限のあるアカウントで再試行する前にリリースを読み直して反映してください。",
        },
        timeout: {
          title: "公開状態を確認できませんでした",
          recovery:
            "再試行する前に変更不可のリリースを読み直してください。同じ冪等な外部反映がすでに受理されている可能性があります。",
        },
        throttled: {
          title: "公開リクエストが保留されました",
          recovery:
            "先にリリースを読み直し、制御プレーンがまだ許可している操作だけを再試行してください。",
        },
        conflict: {
          title: "公開リリースが変更されました",
          recovery:
            "リリースの状態を読み直してください。保存済みの版が変わっていた場合は、続行する前に新しい候補を準備してください。",
        },
        catalog: {
          title: "対象プロファイルカタログを利用できません",
          recovery:
            "既存のリリース証拠はそのまま表示されています。別の対象で候補を準備する前に読み直してください。",
        },
        server: {
          title: "公開の制御プレーンを利用できません",
          recovery:
            "再試行する前にリリースを読み直してください。サーバーが確定した結果を記録するまで、外部反映は失敗と見なされません。",
        },
        network: {
          title: "公開の接続が中断しました",
          recovery:
            "接続を復旧し、あいまいな提供元の応答を整理するために再試行する前にリリースを読み直してください。",
        },
        validation: {
          title: "公開リクエストが拒否されました",
          recovery:
            "残っている障害と正確な対象/証拠の紐づけを確認し、新たに許可された操作だけを実行してください。",
        },
        unknown: {
          title: "公開操作が失敗しました",
          recovery:
            "再試行する前にリリースを読み直し、障害を確認してください。本番は止まったままです。",
        },
      },
    },
    empty: {
      openProjectTitle: "保存済みプロジェクトを開いてください",
      openProjectDetail: "公開候補はサーバーに保存された版に紐づいています。",
      noCandidateTitle: "公開候補がありません",
      noCandidateDetail:
        "環境や配信先を選ぶ前に、いま保存されている版で候補を準備してください。",
    },
    identity: {
      heading: "候補の識別情報",
      release: "リリース",
      revision: "版",
      package: "パッケージ",
      plan: "計画",
      manifest: "構成一覧",
      promotion: "昇格",
      signingHeading: "コンパイラー成果物の署名",
      unsignedNote:
        "候補の範囲 · 署名なし。この識別情報は、公開済みまたは署名済みのコンパイラー成果物を主張しません。",
      compiler: "コンパイラー",
      implementation: "実装",
      artifact: "成果物",
      artifactHash: "成果物のハッシュ",
      scope: "範囲",
      signature: "署名",
      copyHashAria: "コンパイラー成果物のハッシュをコピー",
      copied: "コピーしました",
      copyFailed: "コピーできませんでした",
      copyFullHash: "ハッシュ全体をコピー",
      signingUnavailable:
        "コンパイラー署名の情報がありません。この古いリリースはどのコンパイラーで作られたか記録しておらず、Studio は対象プロファイルから推測しません。",
    },
    targetProfile: {
      label: "対象プロファイル",
      none: "登録された対象プロファイルがありません",
      adapterPrefix: "接続方式:",
      openGaps: (count) => `未解決の本番課題 ${count}件`,
      noGaps: "プロファイルに明記された課題なし",
    },
    authorityCard: {
      heading: "権限と外部反映",
      target: "対象",
      profile: "プロファイル",
      adapter: "接続方式",
      authorization: "本番承認",
      deployment: "配信",
      liveCheck: "実動作チェック",
      openGapsHeading: "未解決の本番課題",
    },
    matrix: {
      heading: "公開検証",
      postDeployNote:
        "本番は内容アドレスで固定されたステージング認証表を使います。証拠のリンクはハッシュに紐づくサーバー記録です。",
      candidateNote:
        "候補の関門と本番の関門は分かれたままです。証拠のリンクはハッシュに紐づくサーバー記録です。",
      candidateGate: "候補",
      productionGate: "本番",
      axisNotProduced: "候補はこの項目の結果をまだ作っていません。",
      axisNoEvidence: "信頼できる証拠が記録されていません。",
      axisStatus: {
        pass: "合格",
        fail: "不合格",
        "not-proven": "未生成",
      },
      axisReportedNotProven: "合格と報告 · 不変の証拠なし",
    },
    proof: {
      summary: "正確な証拠、権限、実動作チェックの詳細",
      contentAddressed: "内容アドレス固定",
      candidateEvidenceHeading: "候補の証拠",
      matrix: "検証表",
      postDeployMatrixRef: "配信後の検証表",
      candidateMatrixRef: "候補の検証表",
      signedRecord: "署名された記録",
      promotion: "昇格",
      candidateGate: "候補の関門",
      productionGate: "本番の関門",
      proofArtifacts: "証拠の成果物",
      proofArtifactCount: (count) => `変更不可の参照 ${count}件`,
      receiptHeading: "実行と配信のレシート",
      environment: "環境",
      operation: "操作",
      adapter: "接続方式",
      status: "ステータス",
      deploymentId: "配信番号",
      receipt: "レシート",
      output: "成果物",
      stagingGrant: "ステージング利用許可",
      authorization: "本番承認",
      verifiedUrlHeading: "検証済み配信アドレス",
      pendingUrlHeading: "提供元がアドレスを返しました · 実動作チェック待ち",
      withheldNote:
        "正確なレシート、成果物、実サーバーの確認がすべて合格するまでアドレスは表示しません。",
      failureReasonsHeading: "失敗の理由",
      liveCheckHeading: "独立した実動作チェック",
      artifactMatch: "成果物の一致",
      liveServer: "実サーバー",
      checked: "確認時刻",
      readback: "読み直し",
      noProofNote:
        "配信や実サーバーの証拠が記録されていません。本番は止まったままです。",
    },
    blockersHeading: "残っている障害",
    environmentLegend: "配信環境",
    staging: "ステージング",
    production: "本番",
    applianceTarget: {
      statement: "このアプライアンスが唯一の配信先です。",
      detail:
        "公開物はこの筐体の中で appliance-local 権限のもとに検証され、配信されます。ここに別の本番サイトはなく、アプライアンスの外への昇格はこの筐体の外で行われます。",
    },
    states: {
        draft: "下書き",
        compiling: "コンパイル中",
        candidate_blocked: "候補が止まっています",
        candidate_ready: "候補の準備ができました",
        verification_running: "検証中",
        verification_blocked: "検証が止まっています",
        certification_candidate: "認証候補",
        authorization_pending: "承認待ち",
        authorized: "承認済み",
        deploying: "配信中",
        deployment_readback_pending: "配信の再確認待ち",
        live_readback_pending: "公開の再確認待ち",
        production_ready: "本番の準備ができました",
        failed: "失敗",
        rolled_back: "戻しました",
        taken_down: "取り下げました",
    },
    candidateReadyNote: "候補は本番開始の画面で使う準備ができました。",
    noLaunchActionNote:
      "いま行える開始操作はありません。障害を確認してリリースを読み直してください。",
    noForwardActionWithRecoveryNote:
      "このリリースを前に進める開始操作はありません。下の操作は、すでに公開中のものを変更します。",
    openVerifiedDeployment: "検証済みの配信を開く",
    rollbackButton: "以前の検証済みレシートに戻す",
    takeDownButton: "検証済みの配信を取り下げる",
    confirm: {
      deployProductionTitle: "本番に配信しますか？",
      deployStagingTitle: "ステージング候補を配信しますか？",
      deployDetail: (profileId, manifestHash, environment) =>
        `${profileId} プロファイルで ${manifestHash} を ${environment} 環境に配信します。`,
      rollbackTitle: "このリリースを戻しますか？",
      rollbackDetail: (receiptHash) =>
        `以前の検証済みレシート ${receiptHash} を復元し、もう一度確認します。`,
      takeDownTitle: (environment) => `この ${environment} 配信を取り下げますか？`,
      takeDownCurrent: "現在の",
      takeDownDetail: (receiptHash) =>
        `レシート ${receiptHash} の経路を閉じます。変更不可のリリース成果物は監査と復旧のために残ります。`,
      closeAria: "確認画面を閉じる",
      serverRecheckNote:
        "サーバーは署名された利用許可または本番承認、元の版、対象、成果物一覧を再確認してから外部に書き込みます。",
      cancel: "キャンセル",
      confirmDeployment: "配信を確定",
      confirmRollback: "戻すことを確定",
      confirmTakeDown: "取り下げを確定",
    },
    ledger: {
      summary: "変更不可のリリース履歴",
      count: (count) => `リリース ${count}件`,
      manifestPrefix: "構成一覧",
      noPrior:
        "以前のリリースはありません。いまの変更不可リリースの識別情報は上にあります。",
      serverOwnedNote:
        "この履歴はサーバーが管理します。ブラウザの保存内容や取り込んだ JSON では、リリースを増やしたり証拠の結果を変えたりできません。",
    },
    actions: {
      prepareCandidate: "候補を準備",
      prepareCandidateBusy: "候補を準備中…",
      prepareForSelectedTarget: "選んだ対象で候補を準備",
      prepareUpdated: "最新の内容で候補を作り直す",
      runMissingChecks: "欠けているチェックを実行",
      runMissingChecksBusy: "検証を実行中…",
      refreshIndependentCheck: "独立チェックをやり直す",
      refreshIndependentCheckBusy: "配信されたリリースを読み込み中…",
      requestStagingGrant: "ステージング利用許可を申請",
      requestStagingGrantBusy: "ステージング利用許可を申請中…",
      deployStaging: "ステージング候補を配信",
      deployStagingBusy: "ステージングに配信中…",
      requestAuthorization: "本番承認を申請",
      requestAuthorizationBusy: "本番承認を申請中…",
      deployProduction: "承認されたリリースを配信",
      deployProductionBusy: "本番リリースを配信中…",
    },
    guards: {
      noAdapter: "選んだ対象プロファイルに登録された配信接続方式がありません。",
      productionNeedsPromotion:
        "本番への配信には、正確なステージング昇格の参照が必要です。",
      deployOutputManifestMissing:
        "このリリースには検証出力マニフェストがもうないため、配信リクエストは送信されませんでした。リリースを読み直し、候補を作り直してください。",
      effectTargetReceiptMissing:
        "この操作の対象レシートがないため、リクエストは送信されませんでした。リリースを読み直してください。",
    },
  },
};

const zh: StudioPublishDeployCopy = {
  publish: {
    eyebrow: "发布 · 候选准备",
    heading: "发布",
    intro:
      "把已保存的项目编成以哈希固定的候选，运行独立检查，然后申请升级。此页面不会向外部部署。",
    askLabel: "查看发布准备情况",
    preflightHeading: "编辑器预检",
    preflightDetail: "这些检查可以拦住候选准备，但不能授权部署。",
    savedProject: "已保存的项目",
    legacyStatus: "旧方式状态",
    slug: "地址名",
    savedVersion: "已保存版本",
    designSystemRelease: "设计系统发布",
    designSystemSnapshot: "设计系统快照",
    verifiedDeployment: "已验证的部署",
    lastProjectUpdate: "最后更新",
    noVerifiedReceipt: "没有已验证的部署凭据",
  },
  launch: {
    eyebrow: "上线 · 已授权的外部生效",
    heading: "上线",
    intro:
      "申请与环境绑定的使用许可或生产授权，部署同一个候选，并完成独立的实时检查后才算就绪。",
    askLabel: "查看上线准备情况",
    diagnosticsSummary: "旧方式项目信息与编辑器预检",
    diagnosticsBadge: "仅供诊断",
    legacyStatus: "旧方式状态",
    slug: "地址名",
    agentRuntimeCard: "应用代理卡片",
    target: "目标",
    savedVersion: "已保存版本",
    verifiedReleaseUrl: "已验证的发布地址",
    noVerifiedReceipt: "没有已验证的部署凭据",
    liveCheck: "实时检查",
    noLiveCheckYet: "还没有实时检查",
    refreshProof: "重新读取证据",
    refreshing: "正在重新读取",
    refreshNeedsSavedProject: "要重新读取验证证据，请打开服务器上已保存的项目。",
    refreshFailed: "无法重新读取服务器证据记录。",
    serviceHealthHeading: "Studio 服务状态",
    serviceHealthDetail:
      "此检查诊断 Studio 服务本身。导入的 JSON 不能替代发布验证项，也不能授权部署。",
  },
  canary: {
    heading: "Studio 服务常态检查",
    localProofReady: "本地证据已就绪",
    evidenceLabel: (source) => `证据来源：${source}`,
    evidenceSources: {
      manualImport: "手动导入",
      proofRecord: "项目证据记录",
      projectMetadata: "项目信息",
      none: "无",
    },
    beforeProductionHeading: "进入生产前",
    beforeProductionItems: [
      "8 个文件的确定性检查全部通过",
      "最后的浏览器证据通过，仅生产常态检查因环境变量而暂缓",
      "创建项目前已明确确认这是生产会话",
    ],
    requiredChecksHeading: "必需检查",
    requiredChecksItems: [
      "创建并注册一个临时的生产 Studio 项目",
      "把 /api/projects/{projectId}/full 作为源数据重新读取",
      "渲染读回的内容、保存截图并记录清理结果",
    ],
    failClosedHeading: "拦停条件",
    failClosedItems: [
      "缺少生产环境变量",
      "生产运行前本地证据出现回退",
      "验证、数据哈希、设计令牌、附带信息或清理证据缺少任一项",
    ],
    missingChecksHeading: "缺少的检查",
    failClosedReasonsHeading: "拦停原因",
    envNamesLabel: "需要的环境变量名",
    provenanceLabel: "来源标记",
    checkOutputLabel: "检查结果文件",
    jsonLabel: "服务检查 JSON",
    jsonPlaceholder:
      "粘贴 output/agentic-rag-final-proof/evidence.json 的内容，不要包含会话令牌",
    importButton: "导入检查",
    clearButton: "清除检查",
    libraryHub: "打开资料库",
    diagnosticNote:
      "仅供诊断：此记录不能替代发布证据，也不能授权部署。会话令牌是运行凭证，只粘贴读回的事实、哈希、来源和清理证据。",
    savedLocallySyncFailed: "检查数据已保存在本机。服务器记录同步失败。",
    clearedLocallySyncFailed: "检查数据已从本机清除。服务器记录同步失败。",
  },
  release: {
    loading: "正在加载发布记录…",
    candidateTitle: "发布候选",
    candidateDetail:
      "准备已保存的版本，确认全部 14 个验证项，然后在不部署的情况下申请升级。",
    launchTitle: "已授权的上线",
    launchDetail: "只部署以哈希固定的候选，并且必须具备所需的使用许可或生产授权。",
    refresh: "重新读取",
    notAvailable: "无值",
    absent: {
      notIssued: "未签发",
      notStarted: "未开始",
      notSelected: "未选择",
      notRegistered: "未注册",
      noCandidate: "无候选",
    },
    gateStatus: {
      ready: "已就绪",
      blocked: "被拦住",
    },
    proofBoolean: {
      match: "已验证一致",
      mismatch: "不一致",
      notChecked: "未检查",
    },
    authority: {
      liveCheckPassed: "实时检查通过",
      rolledBack: "发布已回滚 · 生产保持拦停",
      takenDown: "部署已下线 · 生产保持拦停",
      failClosed: "生产保持拦停",
      passedDetail:
        "已签名的授权、准确的部署凭据、产物一致与实时服务器一致，都已记录在这次发布上。",
      failClosedDetail:
        "候选、已存产物、提供方响应或 HTTP 响应都无法产生生产权限。必须由与凭据绑定的独立实站检查闭合每一项必需证据。",
      productionReadyBadge: "生产就绪",
    },
    errorBanner: {
      recoveryPrefix: "恢复办法：",
      refreshBeforeRetry: "重试前请重新读取发布记录",
      defaultMessage: "发布请求失败。",
      catalogMessage: detail => `无法读取已注册的目标配置，因此无法为新目标准备候选。原因：${detail}`,
      kinds: {
        authentication: {
          title: "需要身份验证",
          recovery:
            "恢复已验证的会话，然后在重试前重新读取并核对此发布。浏览器无法仅凭此响应推断外部生效的结果。",
        },
        permission: {
          title: "没有发布权限",
          recovery:
            "确认项目角色，然后使用有发布权限的账号，在重试前重新读取并核对此发布。",
        },
        timeout: {
          title: "未能确认发布状态",
          recovery:
            "重试前请重新读取不可变发布。同一个幂等的外部生效操作可能已经被接受。",
        },
        throttled: {
          title: "发布请求已被推迟",
          recovery: "先重新读取发布，再重试控制平面目前仍允许的操作。",
        },
        conflict: {
          title: "发布记录已变更",
          recovery:
            "重新读取发布投影。如果已保存的版本发生变化，请在继续前准备新的候选。",
        },
        catalog: {
          title: "目标配置目录不可用",
          recovery: "现有发布证据仍然可见。为其他目标准备候选前请先重新读取。",
        },
        server: {
          title: "发布控制平面不可用",
          recovery:
            "重试前请重新读取发布。在服务器记录出确定结果之前，外部生效不算失败。",
        },
        network: {
          title: "发布连接中断",
          recovery:
            "恢复连接后，为了核对不确定的提供方结果，请在重试前重新读取发布。",
        },
        validation: {
          title: "发布请求被拒绝",
          recovery: "查看剩余阻碍和准确的目标/证据绑定，然后只执行新允许的操作。",
        },
        unknown: {
          title: "发布操作失败",
          recovery: "重试前请重新读取发布并查看其阻碍。生产环境仍处于阻止状态。",
        },
      },
    },
    empty: {
      openProjectTitle: "请打开已保存的项目",
      openProjectDetail: "发布候选绑定在服务器上保存的项目版本。",
      noCandidateTitle: "没有发布候选",
      noCandidateDetail: "在选择环境或部署目标之前，请用当前保存的版本准备候选。",
    },
    identity: {
      heading: "候选身份",
      release: "发布",
      revision: "版本",
      package: "包",
      plan: "计划",
      manifest: "构成清单",
      promotion: "升级",
      signingHeading: "编译器产物签名",
      unsignedNote:
        "候选范围 · 未签名。此身份不声称任何已发布或已签名的编译器产物。",
      compiler: "编译器",
      implementation: "实现",
      artifact: "产物",
      artifactHash: "产物哈希",
      scope: "范围",
      signature: "签名",
      copyHashAria: "复制编译器产物哈希",
      copied: "已复制",
      copyFailed: "复制失败",
      copyFullHash: "复制完整哈希",
      signingUnavailable:
        "没有编译器签名信息。这个较早的发布没有记录由哪个编译器构建，Studio 不会根据目标配置去猜。",
    },
    targetProfile: {
      label: "目标配置",
      none: "没有已注册的目标配置",
      adapterPrefix: "连接方式：",
      openGaps: (count) => `未解决的生产缺口 ${count} 项`,
      noGaps: "配置未声明缺口",
    },
    authorityCard: {
      heading: "权限与外部生效",
      target: "目标",
      profile: "配置",
      adapter: "连接方式",
      authorization: "生产授权",
      deployment: "部署",
      liveCheck: "实时检查",
      openGapsHeading: "未解决的生产缺口",
    },
    matrix: {
      heading: "发布验证",
      postDeployNote:
        "生产使用以内容寻址固定的预发布认证表。证据链接是与哈希绑定的服务器记录。",
      candidateNote:
        "候选关卡与生产关卡仍然分开。证据链接是与哈希绑定的服务器记录。",
      candidateGate: "候选",
      productionGate: "生产",
      axisNotProduced: "候选还没有产出这一项的结果。",
      axisNoEvidence: "没有记录可信证据。",
      axisStatus: {
        pass: "通过",
        fail: "未通过",
        "not-proven": "未生成",
      },
      axisReportedNotProven: "报告为通过 · 无不可变证据",
    },
    proof: {
      summary: "准确证据、权限与实时检查详情",
      contentAddressed: "内容寻址固定",
      candidateEvidenceHeading: "候选证据",
      matrix: "验证表",
      postDeployMatrixRef: "部署后验证表",
      candidateMatrixRef: "候选验证表",
      signedRecord: "已签名记录",
      promotion: "升级",
      candidateGate: "候选关卡",
      productionGate: "生产关卡",
      proofArtifacts: "证据产物",
      proofArtifactCount: (count) => `不可变引用 ${count} 项`,
      receiptHeading: "执行与部署凭据",
      environment: "环境",
      operation: "操作",
      adapter: "连接方式",
      status: "状态",
      deploymentId: "部署编号",
      output: "产物",
      receipt: "凭据",
      stagingGrant: "预发布使用许可",
      authorization: "生产授权",
      verifiedUrlHeading: "已验证的部署地址",
      pendingUrlHeading: "提供方返回了地址 · 等待实时检查",
      withheldNote: "在准确凭据、产物与实时服务器检查全部通过前不显示地址。",
      failureReasonsHeading: "失败原因",
      liveCheckHeading: "独立实时检查",
      artifactMatch: "产物一致",
      liveServer: "实时服务器",
      checked: "检查时间",
      readback: "读回",
      noProofNote: "没有记录部署或实时服务器证据。生产保持拦停。",
    },
    blockersHeading: "剩余阻碍",
    environmentLegend: "部署环境",
    staging: "预发布",
    production: "生产",
    applianceTarget: {
      statement: "本一体机是唯一的部署目标。",
      detail:
        "发布件在本机内以 appliance-local 权限完成校验并对外提供。这里没有单独的生产站点，向一体机之外的晋级在本机之外完成。",
    },
    states: {
        draft: "草稿",
        compiling: "编译中",
        candidate_blocked: "候选受阻",
        candidate_ready: "候选就绪",
        verification_running: "验证进行中",
        verification_blocked: "验证受阻",
        certification_candidate: "认证候选",
        authorization_pending: "等待授权",
        authorized: "已授权",
        deploying: "部署中",
        deployment_readback_pending: "等待部署回读",
        live_readback_pending: "等待线上回读",
        production_ready: "生产就绪",
        failed: "失败",
        rolled_back: "已回滚",
        taken_down: "已下线",
    },
    candidateReadyNote: "候选已可在上线页面使用。",
    noLaunchActionNote: "目前没有可执行的上线操作。请查看阻碍并重新读取发布记录。",
    noForwardActionWithRecoveryNote:
      "此发布没有可继续推进的上线操作。下面的操作会改变已经上线的内容。",
    openVerifiedDeployment: "打开已验证的部署",
    rollbackButton: "回滚到先前已验证的凭据",
    takeDownButton: "下线已验证的部署",
    confirm: {
      deployProductionTitle: "要部署到生产吗？",
      deployStagingTitle: "要部署预发布候选吗？",
      deployDetail: (profileId, manifestHash, environment) =>
        `将以 ${profileId} 配置把 ${manifestHash} 部署到 ${environment} 环境。`,
      rollbackTitle: "要回滚这次发布吗？",
      rollbackDetail: (receiptHash) =>
        `将恢复先前已验证的凭据 ${receiptHash} 并再次读回确认。`,
      takeDownTitle: (environment) => `要下线这个 ${environment} 部署吗？`,
      takeDownCurrent: "当前",
      takeDownDetail: (receiptHash) =>
        `凭据 ${receiptHash} 的路由将被封存。不可变的发布产物仍会保留以便审计与恢复。`,
      closeAria: "关闭确认窗口",
      serverRecheckNote:
        "服务器会在任何外部写入之前，重新核对已签名的使用许可或生产授权、源版本、目标和产物清单。",
      cancel: "取消",
      confirmDeployment: "确认部署",
      confirmRollback: "确认回滚",
      confirmTakeDown: "确认下线",
    },
    ledger: {
      summary: "不可变发布台账",
      count: (count) => `发布 ${count} 条`,
      manifestPrefix: "构成清单",
      noPrior: "没有先前的发布。当前不可变发布身份显示在上方。",
      serverOwnedNote:
        "此历史由服务器掌管。浏览器缓存或导入的 JSON 无法新增发布或改变证据结果。",
    },
    actions: {
      prepareCandidate: "准备候选",
      prepareCandidateBusy: "正在准备候选…",
      prepareForSelectedTarget: "按所选目标准备候选",
      prepareUpdated: "用最新内容重新准备候选",
      runMissingChecks: "运行缺少的检查",
      runMissingChecksBusy: "正在运行验证…",
      refreshIndependentCheck: "重新运行独立检查",
      refreshIndependentCheckBusy: "正在读取已部署的发布…",
      requestStagingGrant: "申请预发布使用许可",
      requestStagingGrantBusy: "正在申请预发布使用许可…",
      deployStaging: "部署预发布候选",
      deployStagingBusy: "正在部署到预发布…",
      requestAuthorization: "申请生产授权",
      requestAuthorizationBusy: "正在申请生产授权…",
      deployProduction: "部署已授权的发布",
      deployProductionBusy: "正在部署生产发布…",
    },
    guards: {
      noAdapter: "所选目标配置没有已注册的部署连接方式。",
      productionNeedsPromotion: "生产部署需要准确的预发布升级引用。",
      deployOutputManifestMissing:
        "该发布不再携带验证输出清单，因此未发送部署请求。请刷新发布并重新准备候选。",
      effectTargetReceiptMissing:
        "该操作的目标回执缺失，因此未发送任何请求。请刷新发布。",
    },
  },
};

const zhTW: StudioPublishDeployCopy = {
  publish: {
    eyebrow: "發布 · 候選準備",
    heading: "發布",
    intro:
      "把已儲存的專案編成以雜湊固定的候選，執行獨立檢查，然後申請升級。此頁面不會對外部署。",
    askLabel: "查看發布準備狀況",
    preflightHeading: "編輯器預檢",
    preflightDetail: "這些檢查可以攔下候選準備，但無法授權部署。",
    savedProject: "已儲存的專案",
    legacyStatus: "舊方式狀態",
    slug: "網址名稱",
    savedVersion: "已儲存版本",
    designSystemRelease: "設計系統發布",
    designSystemSnapshot: "設計系統快照",
    verifiedDeployment: "已驗證的部署",
    lastProjectUpdate: "最後更新",
    noVerifiedReceipt: "沒有已驗證的部署憑據",
  },
  launch: {
    eyebrow: "上線 · 已授權的外部生效",
    heading: "上線",
    intro:
      "申請與環境綁定的使用許可或生產授權，部署同一個候選，並完成獨立的即時檢查後才算就緒。",
    askLabel: "查看上線準備狀況",
    diagnosticsSummary: "舊方式專案資訊與編輯器預檢",
    diagnosticsBadge: "僅供診斷",
    legacyStatus: "舊方式狀態",
    slug: "網址名稱",
    agentRuntimeCard: "應用代理卡片",
    target: "目標",
    savedVersion: "已儲存版本",
    verifiedReleaseUrl: "已驗證的發布網址",
    noVerifiedReceipt: "沒有已驗證的部署憑據",
    liveCheck: "即時檢查",
    noLiveCheckYet: "還沒有即時檢查",
    refreshProof: "重新讀取證據",
    refreshing: "正在重新讀取",
    refreshNeedsSavedProject: "要重新讀取驗證證據，請開啟伺服器上已儲存的專案。",
    refreshFailed: "無法重新讀取伺服器證據記錄。",
    serviceHealthHeading: "Studio 服務狀態",
    serviceHealthDetail:
      "此檢查診斷 Studio 服務本身。匯入的 JSON 不能取代發布驗證項，也不能授權部署。",
  },
  canary: {
    heading: "Studio 服務常態檢查",
    localProofReady: "本機證據已就緒",
    evidenceLabel: (source) => `證據來源：${source}`,
    evidenceSources: {
      manualImport: "手動匯入",
      proofRecord: "專案證據記錄",
      projectMetadata: "專案資訊",
      none: "無",
    },
    beforeProductionHeading: "進入生產前",
    beforeProductionItems: [
      "8 個檔案的確定性檢查全部通過",
      "最後的瀏覽器證據通過，僅生產常態檢查因環境變數而暫緩",
      "建立專案前已明確確認這是生產工作階段",
    ],
    requiredChecksHeading: "必要檢查",
    requiredChecksItems: [
      "建立並註冊一個臨時的生產 Studio 專案",
      "把 /api/projects/{projectId}/full 當作來源資料重新讀取",
      "繪製讀回的內容、保存截圖並記錄清理結果",
    ],
    failClosedHeading: "攔停條件",
    failClosedItems: [
      "缺少生產環境變數",
      "生產執行前本機證據出現退步",
      "驗證、資料雜湊、設計語彙、附帶資訊或清理證據缺少任一項",
    ],
    missingChecksHeading: "缺少的檢查",
    failClosedReasonsHeading: "攔停原因",
    envNamesLabel: "需要的環境變數名稱",
    provenanceLabel: "來源標記",
    checkOutputLabel: "檢查結果檔案",
    jsonLabel: "服務檢查 JSON",
    jsonPlaceholder:
      "貼上 output/agentic-rag-final-proof/evidence.json 的內容，不要包含工作階段憑證",
    importButton: "匯入檢查",
    clearButton: "清除檢查",
    libraryHub: "開啟資料庫",
    diagnosticNote:
      "僅供診斷：此記錄不能取代發布證據，也不能授權部署。工作階段憑證是執行憑據，只貼上讀回的事實、雜湊、來源與清理證據。",
    savedLocallySyncFailed: "檢查資料已存在本機。伺服器記錄同步失敗。",
    clearedLocallySyncFailed: "檢查資料已從本機清除。伺服器記錄同步失敗。",
  },
  release: {
    loading: "正在載入發布記錄…",
    candidateTitle: "發布候選",
    candidateDetail:
      "準備已儲存的版本，確認全部 14 個驗證項，然後在不部署的情況下申請升級。",
    launchTitle: "已授權的上線",
    launchDetail: "只部署以雜湊固定的候選，且必須具備所需的使用許可或生產授權。",
    refresh: "重新讀取",
    notAvailable: "無值",
    absent: {
      notIssued: "未簽發",
      notStarted: "未開始",
      notSelected: "未選擇",
      notRegistered: "未註冊",
      noCandidate: "無候選",
    },
    gateStatus: {
      ready: "已就緒",
      blocked: "被攔下",
    },
    proofBoolean: {
      match: "已驗證一致",
      mismatch: "不一致",
      notChecked: "未檢查",
    },
    authority: {
      liveCheckPassed: "即時檢查通過",
      rolledBack: "發布已回復 · 生產維持攔停",
      takenDown: "部署已下線 · 生產維持攔停",
      failClosed: "生產維持攔停",
      passedDetail:
        "已簽署的授權、準確的部署憑據、產物一致與即時伺服器一致，都已記錄在這次發布上。",
      failClosedDetail:
        "候選、已存產物、供應方回應或 HTTP 回應都無法產生生產權限。必須由與憑據綁定的獨立實站檢查閉合每一項必要證據。",
      productionReadyBadge: "生產就緒",
    },
    errorBanner: {
      recoveryPrefix: "復原辦法：",
      refreshBeforeRetry: "重試前請重新讀取發布記錄",
      defaultMessage: "發布請求失敗。",
      catalogMessage: detail => `無法讀取已註冊的目標設定，因此無法為新目標準備候選。原因：${detail}`,
      kinds: {
        authentication: {
          title: "需要身分驗證",
          recovery:
            "還原已驗證的工作階段，然後在重試前重新讀取並核對這次發布。瀏覽器無法僅憑此回應推斷外部生效的結果。",
        },
        permission: {
          title: "沒有發布權限",
          recovery:
            "確認專案角色，然後使用有發布權限的帳號，在重試前重新讀取並核對這次發布。",
        },
        timeout: {
          title: "未能確認發布狀態",
          recovery:
            "重試前請重新讀取不可變發布。同一個冪等的外部生效操作可能已經被接受。",
        },
        throttled: {
          title: "發布請求已被延後",
          recovery: "先重新讀取發布，再重試控制平面目前仍允許的操作。",
        },
        conflict: {
          title: "發布記錄已變更",
          recovery:
            "重新讀取發布投影。如果已儲存的版本已變更，請在繼續前準備新的候選。",
        },
        catalog: {
          title: "目標設定目錄無法使用",
          recovery: "現有發布證據仍然可見。為其他目標準備候選前請先重新讀取。",
        },
        server: {
          title: "發布控制平面無法使用",
          recovery:
            "重試前請重新讀取發布。在伺服器記錄出確定結果之前，外部生效不算失敗。",
        },
        network: {
          title: "發布連線中斷",
          recovery:
            "還原連線後，為了核對不明確的提供方結果，請在重試前重新讀取發布。",
        },
        validation: {
          title: "發布請求遭拒",
          recovery: "查看剩餘阻礙與準確的目標/證據綁定，然後只執行新允許的操作。",
        },
        unknown: {
          title: "發布操作失敗",
          recovery: "重試前請重新讀取發布並查看其阻礙。正式環境仍處於阻擋狀態。",
        },
      },
    },
    empty: {
      openProjectTitle: "請開啟已儲存的專案",
      openProjectDetail: "發布候選綁定在伺服器上儲存的專案版本。",
      noCandidateTitle: "沒有發布候選",
      noCandidateDetail: "在選擇環境或部署目標之前，請用目前儲存的版本準備候選。",
    },
    identity: {
      heading: "候選身分",
      release: "發布",
      revision: "版本",
      package: "套件",
      plan: "計畫",
      manifest: "組成清單",
      promotion: "升級",
      signingHeading: "編譯器產物簽署",
      unsignedNote:
        "候選範圍 · 未簽署。此身分不主張任何已發布或已簽署的編譯器產物。",
      compiler: "編譯器",
      implementation: "實作",
      artifact: "產物",
      artifactHash: "產物雜湊",
      scope: "範圍",
      signature: "簽署",
      copyHashAria: "複製編譯器產物雜湊",
      copied: "已複製",
      copyFailed: "複製失敗",
      copyFullHash: "複製完整雜湊",
      signingUnavailable:
        "沒有編譯器簽署資訊。這個較早的發布沒有記錄由哪個編譯器建置，Studio 不會依目標設定去猜。",
    },
    targetProfile: {
      label: "目標設定",
      none: "沒有已註冊的目標設定",
      adapterPrefix: "連接方式：",
      openGaps: (count) => `未解決的生產缺口 ${count} 項`,
      noGaps: "設定未宣告缺口",
    },
    authorityCard: {
      heading: "權限與外部生效",
      target: "目標",
      profile: "設定",
      adapter: "連接方式",
      authorization: "生產授權",
      deployment: "部署",
      liveCheck: "即時檢查",
      openGapsHeading: "未解決的生產缺口",
    },
    matrix: {
      heading: "發布驗證",
      postDeployNote:
        "生產使用以內容定址固定的預備認證表。證據連結是與雜湊綁定的伺服器記錄。",
      candidateNote:
        "候選關卡與生產關卡仍然分開。證據連結是與雜湊綁定的伺服器記錄。",
      candidateGate: "候選",
      productionGate: "生產",
      axisNotProduced: "候選還沒有產出這一項的結果。",
      axisNoEvidence: "沒有記錄可信證據。",
      axisStatus: {
        pass: "通過",
        fail: "未通過",
        "not-proven": "未生成",
      },
      axisReportedNotProven: "回報為通過 · 無不可變證據",
    },
    proof: {
      summary: "準確證據、權限與即時檢查詳情",
      contentAddressed: "內容定址固定",
      candidateEvidenceHeading: "候選證據",
      matrix: "驗證表",
      postDeployMatrixRef: "部署後驗證表",
      candidateMatrixRef: "候選驗證表",
      signedRecord: "已簽署記錄",
      promotion: "升級",
      candidateGate: "候選關卡",
      productionGate: "生產關卡",
      proofArtifacts: "證據產物",
      proofArtifactCount: (count) => `不可變引用 ${count} 項`,
      receiptHeading: "執行與部署憑據",
      environment: "環境",
      operation: "操作",
      adapter: "連接方式",
      status: "狀態",
      deploymentId: "部署編號",
      output: "產物",
      receipt: "憑據",
      stagingGrant: "預備使用許可",
      authorization: "生產授權",
      verifiedUrlHeading: "已驗證的部署網址",
      pendingUrlHeading: "供應方回傳了網址 · 等待即時檢查",
      withheldNote: "在準確憑據、產物與即時伺服器檢查全部通過前不顯示網址。",
      failureReasonsHeading: "失敗原因",
      liveCheckHeading: "獨立即時檢查",
      artifactMatch: "產物一致",
      liveServer: "即時伺服器",
      checked: "檢查時間",
      readback: "讀回",
      noProofNote: "沒有記錄部署或即時伺服器證據。生產維持攔停。",
    },
    blockersHeading: "剩餘阻礙",
    environmentLegend: "部署環境",
    staging: "預備",
    production: "生產",
    applianceTarget: {
      statement: "本設備是唯一的部署目標。",
      detail:
        "發布件在本機內以 appliance-local 權限完成驗證並對外提供。這裡沒有單獨的生產站點，向設備之外的晉級在本機之外完成。",
    },
    states: {
        draft: "草稿",
        compiling: "編譯中",
        candidate_blocked: "候選受阻",
        candidate_ready: "候選就緒",
        verification_running: "驗證進行中",
        verification_blocked: "驗證受阻",
        certification_candidate: "認證候選",
        authorization_pending: "等待授權",
        authorized: "已授權",
        deploying: "部署中",
        deployment_readback_pending: "等待部署回讀",
        live_readback_pending: "等待線上回讀",
        production_ready: "生產就緒",
        failed: "失敗",
        rolled_back: "已回復",
        taken_down: "已下線",
    },
    candidateReadyNote: "候選已可在上線頁面使用。",
    noLaunchActionNote: "目前沒有可執行的上線操作。請查看阻礙並重新讀取發布記錄。",
    noForwardActionWithRecoveryNote:
      "此發布沒有可繼續推進的上線操作。下面的操作會改變已經上線的內容。",
    openVerifiedDeployment: "開啟已驗證的部署",
    rollbackButton: "回復到先前已驗證的憑據",
    takeDownButton: "下線已驗證的部署",
    confirm: {
      deployProductionTitle: "要部署到生產嗎？",
      deployStagingTitle: "要部署預備候選嗎？",
      deployDetail: (profileId, manifestHash, environment) =>
        `將以 ${profileId} 設定把 ${manifestHash} 部署到 ${environment} 環境。`,
      rollbackTitle: "要回復這次發布嗎？",
      rollbackDetail: (receiptHash) =>
        `將復原先前已驗證的憑據 ${receiptHash} 並再次讀回確認。`,
      takeDownTitle: (environment) => `要下線這個 ${environment} 部署嗎？`,
      takeDownCurrent: "目前",
      takeDownDetail: (receiptHash) =>
        `憑據 ${receiptHash} 的路由將被封存。不可變的發布產物仍會保留以便稽核與復原。`,
      closeAria: "關閉確認視窗",
      serverRecheckNote:
        "伺服器會在任何外部寫入之前，重新核對已簽署的使用許可或生產授權、來源版本、目標與產物清單。",
      cancel: "取消",
      confirmDeployment: "確認部署",
      confirmRollback: "確認回復",
      confirmTakeDown: "確認下線",
    },
    ledger: {
      summary: "不可變發布台帳",
      count: (count) => `發布 ${count} 筆`,
      manifestPrefix: "組成清單",
      noPrior: "沒有先前的發布。目前不可變發布身分顯示在上方。",
      serverOwnedNote:
        "此歷史由伺服器掌管。瀏覽器快取或匯入的 JSON 無法新增發布或改變證據結果。",
    },
    actions: {
      prepareCandidate: "準備候選",
      prepareCandidateBusy: "正在準備候選…",
      prepareForSelectedTarget: "按所選目標準備候選",
      prepareUpdated: "用最新內容重新準備候選",
      runMissingChecks: "執行缺少的檢查",
      runMissingChecksBusy: "正在執行驗證…",
      refreshIndependentCheck: "重新執行獨立檢查",
      refreshIndependentCheckBusy: "正在讀取已部署的發布…",
      requestStagingGrant: "申請預備使用許可",
      requestStagingGrantBusy: "正在申請預備使用許可…",
      deployStaging: "部署預備候選",
      deployStagingBusy: "正在部署到預備環境…",
      requestAuthorization: "申請生產授權",
      requestAuthorizationBusy: "正在申請生產授權…",
      deployProduction: "部署已授權的發布",
      deployProductionBusy: "正在部署生產發布…",
    },
    guards: {
      noAdapter: "所選目標設定沒有已註冊的部署連接方式。",
      productionNeedsPromotion: "生產部署需要準確的預備升級引用。",
      deployOutputManifestMissing:
        "該發布不再攜帶驗證輸出清單，因此未發送部署請求。請重新整理發布並重新準備候選。",
      effectTargetReceiptMissing:
        "該操作的目標回執缺失，因此未發送任何請求。請重新整理發布。",
    },
  },
};

const COPY: Record<LocaleCode, StudioPublishDeployCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getStudioPublishDeployCopy(
  locale: LocaleCode,
): StudioPublishDeployCopy {
  return COPY[locale];
}
